<template>
  <div>
    <v-dialog v-model="isShowDialog" persistent scrollable max-width="400">
      <v-card max-height="60vh">
        <v-card-text class="ma-0 px-2 py-0">
          <v-list dense>
            <div
              v-for="obs in draftObservatories"
              :key="`${obs.type}-${obs.id}`"
            >
              <v-list-item link @click="selectDraftObservatory(obs)">
                <v-list-item-icon>
                  <v-icon small>{{ obs.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ obs.name }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
            </div>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="isShowDialog = false">
            キャンセル
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { OBSERVATORY } from "../../../enums/Type";
import { MapPane } from "../../../enums/Type";
import { isPublicMode } from "../../../utils/common";
import L from "leaflet";
import "leaflet-markers-canvas";

const ZOOM_ICON_HIDE = 8;

export default {
  name: "ObservatoryLayer",
  data() {
    return {
      obsClicked: false,
      markers: [],
      canvasMarkers: [],
      draftObservatories: [],
      isShowDialog: false,
      tooltipLayer: null,
      markerLayer: null,
      manholeMarkerLayer: null
    };
  },
  computed: {
    targetTypes() {
      let targets = this.$store.getters.riskSubControl.observatory;
      if (isPublicMode === true) {
        targets = targets.filter(t => t !== OBSERVATORY.PUMP.code);
      }
      return targets;
    },
    observatories() {
      const observatories = JSON.parse(
        JSON.stringify(this.$store.getters.observatories)
      );
      return observatories.filter(obs => {
        return this.targetTypes.includes(obs.type);
      });
    },
    baseDate() {
      return this.$store.getters.baseDate;
    },
    map() {
      return this.$store.getters.leafletMap;
    },
    zoom() {
      return Math.floor(this.map.getZoom());
    }
  },
  watch: {
    baseDate(newVal, oldVal) {
      if (!newVal.isSame(oldVal)) {
        this.fetchObservatories();
      }
    },
    observatories(newVal, oldVal) {
      if (newVal.length === 0 && oldVal.length === 0) {
        return;
      }
      this.applyFilteredObservatories();
    },
    zoom(newVal, oldVal) {
      if (newVal === oldVal) {
        return;
      }
      if (
        (oldVal <= ZOOM_ICON_HIDE && newVal > ZOOM_ICON_HIDE) ||
        (oldVal > ZOOM_ICON_HIDE && newVal <= ZOOM_ICON_HIDE)
      ) {
        this.applyFilteredObservatories();
      }
    }
  },
  created() {
    // tooltip layer
    this.tooltipLayer = L.layerGroup();
    this.tooltipLayer.id = "tooltiplayer";
    this.map.addLayer(this.tooltipLayer);

    // marker layer
    this.markerLayer = new L.MarkersCanvas({
      pane: MapPane.ObservatoryMarkersLayer.name
    });
    this.markerLayer.id = "observatorylayer";
    this.map.addLayer(this.markerLayer);

    // manhole marker layer (DOM marker for halo style)
    this.manholeMarkerLayer = L.layerGroup();
    this.manholeMarkerLayer.id = "manholehalolayer";
    this.map.addLayer(this.manholeMarkerLayer);

    this.$store.commit("SET_IS_LOADING", true);
    this.fetchObservatories();
  },
  mounted() {
    setTimeout(() => {
      this.$store.commit("SET_IS_LOADING", false);
    }, 1500);
  },
  beforeDestroy() {
    this.map.eachLayer(layer => {
      if (
        layer.id === "observatorylayer" ||
        layer.id === "tooltiplayer" ||
        layer.id === "manholehalolayer"
      ) {
        this.map.removeLayer(layer);
      }
    });
    this.$store.commit("SET_USAGE_GUIDE", {
      usageGuide: {
        title: null,
        colors: []
      }
    });
  },
  methods: {
    fetchObservatories() {
      this.$store.dispatch("FETCH_OBSERVATORIES");
    },
    addTooltip(obs, marker) {
      let direction = "top";
      let offset = [0, -15];
      if (
        (obs.code === "3841_191" && obs.type === 4) ||
        (obs.code === "3841_194" && obs.type === 4)
      ) {
        direction = "right";
        offset = [15, 0];
      } else if (
        (obs.code === "9999_2" && obs.type === 30) ||
        (obs.code === "9999_3" && obs.type === 30)
      ) {
        direction = "left";
        offset = [-15, 0];
      }
      var tooltip = L.tooltip({
        direction: direction,
        offset: offset,
        permanent: marker === null
      });
      let osname = obs.name;
      if (obs.type === OBSERVATORY.PUMP.code && obs.tooltip) {
        osname = `${obs.name}<br>${obs.tooltip}`;
      }
      tooltip.setContent(osname);
      tooltip.setLatLng([obs.lat, obs.lng]);
      if (marker === null) {
        tooltip.addTo(this.tooltipLayer);
      } else {
        marker.bindTooltip(tooltip);
      }
    },
    applyFilteredObservatories() {
      // Clear Layer
      this.tooltipLayer.clearLayers();
      this.canvasMarkers.forEach(m => this.markerLayer.removeMarker(m, false));
      this.canvasMarkers = [];
      this.manholeMarkerLayer.clearLayers();
      this.markers = [];

      if (this.zoom <= ZOOM_ICON_HIDE || this.observatories.length === 0) {
        this.markerLayer.redraw();
        return;
      }
      // Add Markers
      this.observatories.forEach(obs => {
        if (obs.lat === undefined || obs.lng === undefined) {
          return;
        }
        const iconUrl = require(`@/assets/images/observatory-icons/${this.getIconUrl(
          obs
        )}`);
        let iconSize = 24;
        const anchor = [iconSize * 0.5, iconSize * 0.5];
        let adjusted = false;
        if (obs.type === 1) {
          const finded = this.observatories.find(
            o =>
              o.type === 4 &&
              Math.abs(o.lat - obs.lat) <= 0.0001 &&
              Math.abs(o.lng - obs.lng) <= 0.0001
          );
          if (finded !== undefined) {
            anchor[0] -= iconSize;
            adjusted = true;
          }
        } else if (obs.type === 30) {
          const finded = this.observatories.find(
            o =>
              o.type !== obs.type &&
              o.type !== 50 &&
              o.type !== 60 &&
              Math.abs(o.lat - obs.lat) <= 0.0001 &&
              Math.abs(o.lng - obs.lng) <= 0.0001
          );
          if (finded !== undefined) {
            anchor[1] -= iconSize;
            adjusted = true;
          }
        } else if (obs.type === 50) {
          const finded = this.observatories.find(
            o =>
              o.type !== obs.type &&
              Math.abs(o.lat - obs.lat) <= 0.0001 &&
              Math.abs(o.lng - obs.lng) <= 0.0001
          );
          if (finded !== undefined) {
            anchor[0] += iconSize;
            adjusted = true;
          }
        }
        const latLng = L.latLng([obs.lat, obs.lng]);
        const isHaloDanger =
          (obs.type === OBSERVATORY.MANHOLE.code ||
            obs.type === OBSERVATORY.MANHOLE_WATERWAY.code) &&
          obs.forecastStatus === "dangerous";
        const isHaloWarning =
          (obs.type === OBSERVATORY.MANHOLE.code ||
            obs.type === OBSERVATORY.MANHOLE_WATERWAY.code) &&
          obs.forecastStatus === "warning";
        const icon = L.icon({
          iconSize: [iconSize, iconSize],
          iconAnchor: anchor,
          iconUrl: iconUrl,
          className: isHaloDanger
            ? "manhole-halo-icon-danger"
            : isHaloWarning
            ? "manhole-halo-icon-warning"
            : ""
        });
        const marker = L.marker(latLng, {
          icon: icon,
          observatoryInfo: obs,
          adjusted: adjusted
        });
        this.addTooltip(obs, marker);
        marker.on("click", e => this.selectObservatory(obs, e));
        if (obs.type === OBSERVATORY.MANHOLE.code) {
          marker.addTo(this.manholeMarkerLayer);
        } else {
          this.canvasMarkers.push(marker);
        }
        this.markers.push(marker);
      });
      if (this.markers.length === 0) {
        return;
      }
      // add markders
      if (this.canvasMarkers.length > 0) {
        this.markerLayer.addMarkers(this.canvasMarkers);
      }
      // レンダリング位置がずれる対策
      this.map.panTo(this.map.getCenter());
    },
    getIconUrl(observatoryInfo) {
      switch (observatoryInfo.type) {
        case OBSERVATORY.STAGE.code:
          switch (observatoryInfo.status) {
            case "deficit":
              return "stage-deficit.png";
            case "normal":
              return "stage-normal.png";
            case "standby":
              return "stage-standby.png";
            case "warning":
              return "stage-warning.png";
            case "evacuation":
              return "stage-evacuation.png";
            case "dangerous":
              return "stage-dangerous.png";
            case "outbreak":
              return "stage-outbreak.png";
            default:
              return "stage-default.png";
          }
        case OBSERVATORY.RAIN.code:
          switch (observatoryInfo.status) {
            case "deficit":
              return "rain-deficit.png";
            case "0":
              return "rain-0.png";
            case "1":
              return "rain-1.png";
            case "2":
              return "rain-2.png";
            case "3":
              return "rain-3.png";
            case "4":
              return "rain-4.png";
            default:
              return "rain-default.png";
          }
        case OBSERVATORY.DAM.code:
          switch (observatoryInfo.status) {
            case "deficit":
              return "dam-deficit.png";
            case "normal":
              return "dam-normal.png";
            default:
              return "dam-default.png";
          }
        case OBSERVATORY.CAMERA.code:
          return "camera-normal.png";
        case OBSERVATORY.LIVECAMERA.code:
          return "camera-iris.png";
        case OBSERVATORY.OVERFLOW_WATERWAY.code:
          switch (observatoryInfo.status) {
            case "normal":
              return "overflow-waterway-normal.png";
            case "warning":
              return "overflow-waterway-warning.png";
            case "dangerous":
              return "overflow-waterway-dangerous.png";
            default:
              return "overflow-waterway-deficit.png";
          }
        case OBSERVATORY.SUIJIN.code:
          switch (observatoryInfo.status) {
            case "topFlooded":
              return "suijin-top-flooded.png";
            case "flooded":
              return "suijin-flooded.png";
            case "waiting":
              return "suijin-waiting.png";
            default:
              return isPublicMode
                ? "suijin-waiting.png"
                : "suijin-maintenace.png";
          }
        case OBSERVATORY.MANHOLE.code:
          return `manhole-${observatoryInfo.status}.png`;
        case OBSERVATORY.MANHOLE_WATERWAY.code:
          return `manhole-waterway-${observatoryInfo.status}.png`;
        case OBSERVATORY.PUMP.code:
          switch (observatoryInfo.status) {
            case "driving":
              return "pump-driving.png";
            case "stop":
              return "pump-stop.png";
            case "failure":
              return "pump-failure.png";
            default:
              return "pump-deficit.png";
          }
      }
    },
    selectObservatory(observatory, event) {
      // 二重起動防止
      if (this.obsClicked) {
        return;
      }
      this.obsClicked = true;
      setTimeout(() => {
        this.obsClicked = false;
      }, 500);

      if (this.zoom >= 15) {
        this.$store.dispatch("FETCH_SELECTED_OBSERVATORY_DATA", observatory);
        return;
      }

      const clickPoint = this.map.latLngToContainerPoint(
        event.latlng || event.target._latlng
      );
      const targets = [];
      this.markers.forEach(marker => {
        const markerPoint = this.map.latLngToContainerPoint(marker.getLatLng());
        const distance = clickPoint.distanceTo(markerPoint);
        if (distance <= 10) {
          targets.push(marker.options.observatoryInfo);
        }
      });

      if (targets.length <= 1) {
        this.$store.dispatch("FETCH_SELECTED_OBSERVATORY_DATA", observatory);
        return;
      }

      this.draftObservatories = JSON.parse(JSON.stringify(targets));
      this.draftObservatories.forEach(obs => {
        switch (obs.type) {
          case OBSERVATORY.RAIN.code:
            obs.icon = "mdi-checkbox-blank-circle";
            break;
          case OBSERVATORY.STAGE.code:
            obs.icon = "mdi-triangle";
            break;
          case OBSERVATORY.CAMERA.code:
            obs.icon = "mdi-camera";
            break;
          case OBSERVATORY.LIVECAMERA.code:
            obs.icon = "mdi-camera-iris";
            break;
          case OBSERVATORY.DAM.code:
            obs.icon = "mdi-car-windshield";
            break;
          case OBSERVATORY.OVERFLOW_WATERWAY.code:
            obs.icon = "mdi-alpha-l-box";
            break;
          case OBSERVATORY.SUIJIN.code:
            obs.icon = "mdi-alpha-s-circle";
            break;
          case OBSERVATORY.MANHOLE.code:
            obs.icon = "mdi-alpha-m-circle";
            break;
          case OBSERVATORY.MANHOLE_WATERWAY.code:
            obs.icon = "mdi-bitbucket";
            break;
          case OBSERVATORY.PUMP.code:
            obs.icon = "mdi-water-pump";
            break;
        }
      });
      setTimeout(() => {
        this.isShowDialog = true;
      }, 300);
    },
    selectDraftObservatory(observatoryInfo) {
      this.$store.dispatch("FETCH_SELECTED_OBSERVATORY_DATA", observatoryInfo);
      this.isShowDialog = false;
    }
  }
};
</script>

<style>
.manhole-halo-icon-warning {
  filter: drop-shadow(0 0 1px rgba(255, 255, 255, 1))
    drop-shadow(0 0 2px rgba(255, 150, 0, 1))
    drop-shadow(0 0 5px rgba(255, 150, 0, 1))
    drop-shadow(0 0 9px rgba(255, 150, 0, 0.95));
}
.manhole-halo-icon-danger {
  filter: drop-shadow(0 0 1px rgba(255, 255, 255, 1))
    drop-shadow(0 0 2px rgba(200, 0, 255, 1))
    drop-shadow(0 0 5px rgba(200, 0, 255, 1))
    drop-shadow(0 0 9px rgba(200, 0, 255, 0.95));
}
</style>
